import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useCurrentUser } from '@/lib/useCurrentUser';
import type { ChecklistEvent, TechRef } from '@/lib/teamwork';

export const CHECKLIST_EVENTS_KEY = ['assembly-order-checklist-events'] as const;

export async function fetchChecklistEvents(orderId: string): Promise<ChecklistEvent[]> {
  const { data, error } = await supabase
    .from('assembly_order_checklist_events')
    .select('*, technician:technicians!technician_id(name, initials, alias)')
    .eq('assembly_order_id', orderId)
    .order('created_at', { ascending: true })
    .order('id', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ChecklistEvent[];
}

/** Every tick and untick on an order, oldest first. */
export function useChecklistEvents(orderId: string | null | undefined) {
  return useQuery({
    queryKey: [...CHECKLIST_EVENTS_KEY, orderId],
    queryFn: () => fetchChecklistEvents(orderId!),
    enabled: !!orderId,
    staleTime: 15_000,
  });
}

export interface MyTeam {
  /** The current technician's manager; null = works alone. */
  managedBy: string | null;
  /** Active technicians with the same manager, the current one included. */
  members: TechRef[];
}

/**
 * The current technician's team: active technicians who share their manager
 * (the database's rule, current_user_on_order_team). Empty for anyone with
 * no manager.
 */
export function useMyTeam() {
  const { data: me } = useCurrentUser();
  const myId = me?.id ?? null;
  return useQuery({
    queryKey: ['my-team', myId],
    enabled: !!myId,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<MyTeam> => {
      const { data: self, error } = await supabase
        .from('technicians')
        .select('managed_by')
        .eq('id', myId!)
        .maybeSingle();
      if (error) throw error;
      const managedBy = (self?.managed_by as string | null) ?? null;
      if (!managedBy) return { managedBy: null, members: [] };

      const { data: members, error: mErr } = await supabase
        .from('technicians')
        .select('id, name, initials, alias')
        .eq('managed_by', managedBy)
        .eq('role', 'technician')
        .eq('is_active', true)
        .order('name');
      if (mErr) throw mErr;
      return { managedBy, members: (members ?? []) as TechRef[] };
    },
  });
}

/** Names for a set of technician ids (builders, submitters...). */
export function useTechnicianNames(ids: ReadonlyArray<string>) {
  const key = [...new Set(ids)].sort();
  return useQuery({
    queryKey: ['technician-names', key.join(',')],
    enabled: key.length > 0,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('technicians')
        .select('id, name, initials, alias')
        .in('id', key);
      if (error) throw error;
      return new Map((data ?? []).map((t: any) => [t.id as string, t as TechRef]));
    },
  });
}
